import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { PageHeaderWithBack } from "../PageHeaderWithBack";
import { PixTransferPasswordStep } from "../PixTransferSteps/PixTransferPasswordStep";
import {
  confirmInternalTransfer,
} from "../../../Hooks/TransferTransaction";
import {
  ProgressOperationError,
  useProgressAlert,
} from "../../../Hooks/useProgressAlert";
import { isFidoStepUpPassword } from "../../../Hooks/useFidoStepUp";
import { usevalutAccount } from "../../../Hooks/ValutAccounts";
import useActiveIbAccount from "../../../Hooks/useActiveIbAccount";
import { useTransferAccountBalance } from "../../../Hooks/useTransferAccountBalance";
import { useBalance } from "../../../Hooks/useBalance";
import { getAccountTypeLabel, getStoredSelectedAccountId } from "../../../Constants/accountTypes";
import { convertToCents } from "../../../Utils/formatters";
import { isSameIbAccount } from "../../../Utils/isSameIbAccount";
import { cleanTaxIDMask } from "../../../Utils/masks";
import {
  INTERNAL_TRANSFER_FORM_ID,
  INTERNAL_TRANSFER_SEARCH_FORM_ID,
  InternalDestinationStep,
  InternalTransferConfirmStep,
  InternalTransferInfoStep,
} from "./valutStep";
import { InternalTransferStepper } from "./InternalTransferStepper";
import { FinalStep } from "./finalStep";

export {
  INTERNAL_TRANSFER_FORM_ID,
  INTERNAL_TRANSFER_SEARCH_FORM_ID,
} from "./valutStep";

export const SELF_TRANSFER_MESSAGE =
  "Não é possível transferir para a mesma conta de origem. Selecione outra conta Valut.";

const STEP_SUBTITLES = [
  "Informe o CPF ou CNPJ do destinatário.",
  "Informe o valor e a descrição da transferência.",
  "Revise os dados antes de confirmar.",
  "Informe sua senha transacional para concluir.",
  "Transferência finalizada.",
];

const requestFormSubmit = (formId) => {
  const form = document.getElementById(formId);
  if (form instanceof HTMLFormElement) {
    form.requestSubmit();
  }
};

export const TransferStepper = forwardRef(function TransferStepper(
  {
    onClose,
    initialType = "internal",
    inModal = false,
    footerInModal = false,
    isActive = true,
    onStepChange,
  },
  ref
) {
  const passwordStepRef = useRef(null);
  const searchRequestedRef = useRef(false);
  const wasActiveRef = useRef(isActive);
  const isConfirmingRef = useRef(false);
  const finalBalanceRefreshDone = useRef(false);
  const progress = useProgressAlert();

  const [step, setStep] = useState(0);
  const [taxId, setTaxId] = useState("");
  const [linkValue, setLinkValue] = useState("");
  const [transferDescription, setTransferDescription] = useState("");
  const [transferValue, setTransferValue] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [transactionSucceeded, setTransactionSucceeded] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [sourceAccount] = useState(getStoredSelectedAccountId);

  const {
    state: contactData,
    handleGET: fetchAccounts,
    loading: bcAccountLoading,
    success: isContactSuccess,
    message: contactMessage,
  } = usevalutAccount();

  const { account: activeAccount } = useActiveIbAccount();
  const { getBalance: refreshBalance } = useBalance();
  const {
    spendable: sourceAccountBalanceCents,
    formattedBalance: sourceAccountBalance,
    formattedAvailable: sourceAccountFormattedAvailable,
    formattedLimit: sourceAccountFormattedLimit,
    showLimit: sourceAccountShowLimit,
    loading: sourceAccountBalanceLoading,
    error: sourceAccountBalanceError,
    refresh: refreshSourceAccountBalance,
  } = useTransferAccountBalance(sourceAccount);

  const isSelfDestination = useMemo(
    () => isSameIbAccount(contactData, activeAccount),
    [contactData, activeAccount],
  );

  const searchSettled =
    !bcAccountLoading.GET && isContactSuccess.GET !== null;

  const lookupError = contactMessage?.GET;
  const isSelfTransferError =
    lookupError?.error === "transfer_to_self" ||
    lookupError?.information === "Transferência não permitida" ||
    (isContactSuccess.GET === true && isSelfDestination);

  const showNotFound =
    searchSettled &&
    (isContactSuccess.GET === false || !contactData?.name || isSelfDestination);

  const notFoundTitle = isSelfTransferError
    ? "Transferência não permitida"
    : "Conta não encontrada";

  const notFoundMessage = isSelfTransferError
    ? lookupError?.description ||
      lookupError?.detail ||
      SELF_TRANSFER_MESSAGE
    : undefined;

  const resetFlow = useCallback(() => {
    setStep(0);
    setTaxId("");
    setLinkValue("");
    setTransferDescription("");
    setTransferValue(0);
    setIsConfirming(false);
    isConfirmingRef.current = false;
    setReceiptData(null);
    setTransactionSucceeded(null);
    setPasswordError(null);
    finalBalanceRefreshDone.current = false;
    searchRequestedRef.current = false;
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      goBack: () => {
        if (step === 1) {
          setStep(0);
          return;
        }
        if (step === 2) {
          setStep(1);
          return;
        }
        if (step === 3) {
          setPasswordError(null);
          setStep(2);
        }
      },
      submitSearch: () => requestFormSubmit(INTERNAL_TRANSFER_SEARCH_FORM_ID),
      submitTransferInfo: () => {
        if (isSelfDestination) return;
        requestFormSubmit(INTERNAL_TRANSFER_FORM_ID);
      },
      submit: () => {
        if (isSelfDestination) return;
        if (step === 2) {
          setStep(3);
        }
      },
      confirmPassword: () => {
        if (isSelfDestination) return;
        passwordStepRef.current?.confirm();
      },
      reset: resetFlow,
      isSelfDestination: () => isSelfDestination,
    }),
    [step, resetFlow, isSelfDestination]
  );

  useEffect(() => {
    if (wasActiveRef.current && !isActive) {
      resetFlow();
    }
    wasActiveRef.current = isActive;
  }, [isActive, resetFlow]);

  useEffect(() => {
    if (step < 4) {
      finalBalanceRefreshDone.current = false;
      return;
    }
    if (!receiptData || finalBalanceRefreshDone.current) return;

    finalBalanceRefreshDone.current = true;
    refreshSourceAccountBalance();
    refreshBalance();
  }, [step, receiptData, refreshSourceAccountBalance, refreshBalance]);

  const isSearching = Boolean(bcAccountLoading.GET);

  useEffect(() => {
    onStepChange?.({
      step,
      transactionStatus: transactionSucceeded,
      isConfirming,
      isSearching,
      isSelfDestination,
    });
  }, [
    step,
    transactionSucceeded,
    isConfirming,
    isSearching,
    isSelfDestination,
    onStepChange,
  ]);

  const handleSearch = useCallback(
    async (document) => {
      searchRequestedRef.current = true;
      progress.showProgress({
        message: "Buscando conta...",
        description: "Consultando destinatário Valut.",
      });
      try {
        await fetchAccounts({ doc: document });
      } catch {
        searchRequestedRef.current = false;
      } finally {
        progress.hideProgress();
      }
    },
    [fetchAccounts, progress]
  );

  useEffect(() => {
    if (!searchRequestedRef.current) return;
    if (bcAccountLoading.GET) return;
    if (isContactSuccess.GET === null) return;

    if (isContactSuccess.GET === true) {
      if (contactData?.name) {
        searchRequestedRef.current = false;
        // When active account is known and matches destination, stay on step 0 with error.
        // If active account is still loading, advance — step 1 re-validates and disables Continuar.
        if (activeAccount && isSameIbAccount(contactData, activeAccount)) {
          return;
        }
        setStep(1);
      } else if (contactData !== undefined) {
        // Settled without a usable account (not found or empty body).
        searchRequestedRef.current = false;
      }
      return;
    }

    searchRequestedRef.current = false;
  }, [activeAccount, bcAccountLoading.GET, contactData, isContactSuccess.GET]);

  const handleTransferInfoContinue = useCallback(({ value, description }) => {
    if (isSelfDestination) return;
    setLinkValue(value);
    setTransferDescription(description);
    setTransferValue(convertToCents(value));
    setStep(2);
  }, [isSelfDestination]);

  const handleFinishTransaction = useCallback(
    async (pass) => {
      if (
        isSelfDestination ||
        isConfirmingRef.current ||
        (!isFidoStepUpPassword(pass) && pass.length !== 4)
      ) {
        return { success: false };
      }

      isConfirmingRef.current = true;
      setIsConfirming(true);
      setPasswordError(null);

      const taxIDData = cleanTaxIDMask(taxId);
      const beneficiaryName = contactData?.name ?? "destinatário";

      try {
        const result = await progress.runWithProgress(
          () =>
            confirmInternalTransfer({
              amount: transferValue,
              description: transferDescription ? transferDescription : null,
              account_id: contactData?.account_id || undefined,
              target: {
                institution_code: "000",
                type: "internal",
                account: contactData?.account_code
                  ? {
                      account_code: contactData.account_code,
                      account_digit: contactData.account_digit,
                      branch_code: contactData.branch_code,
                    }
                  : undefined,
                entity: {
                  name: contactData?.name,
                  document: taxIDData,
                  document_type: taxIDData.length > 11 ? "cnpj" : "cpf",
                },
                validation: {
                  password_combination: pass,
                },
              },
            }),
          {
            loading: "Finalizando transferência...",
            loadingDescription: `Enviando para ${beneficiaryName}`,
            success: "Transferência concluída",
            successDescription: "Entre contas Valut.",
            error: "Falha na transferência",
            errorDescription:
              "Não foi possível concluir a transferência. Tente novamente.",
          }
        );

        setReceiptData(result?.data ?? null);
        setTransactionSucceeded(true);
        setStep(4);
        return { success: true };
      } catch (error) {
        if (error instanceof ProgressOperationError) {
          const nextError = {
            title: error.result.message ?? "Aviso",
            description:
              error.result.description ??
              "Não foi possível concluir a transferência. Tente novamente.",
          };
          setPasswordError(nextError);
          setTransactionSucceeded(false);
          return { success: false, ...nextError };
        }

        const nextError = {
          title: "Falha na transferência",
          description:
            error instanceof Error
              ? error.message
              : "Não foi possível concluir a transferência.",
        };
        setPasswordError(nextError);
        setTransactionSucceeded(false);
        return { success: false, ...nextError };
      } finally {
        isConfirmingRef.current = false;
        setIsConfirming(false);
      }
    },
    [
      contactData,
      isSelfDestination,
      progress,
      taxId,
      transferDescription,
      transferValue,
    ]
  );

  const handleClose = () => {
    resetFlow();
    onClose();
  };

  if (initialType !== "internal") {
    return null;
  }

  const sourceAccountLabel = getAccountTypeLabel(sourceAccount);

  const wizardContent =
    step < 4 ? (
      <>
        <InternalTransferStepper currentStep={step} />

        {step === 0 && (
          <InternalDestinationStep
            taxId={taxId}
            onTaxIdChange={setTaxId}
            onSearch={handleSearch}
            showNotFound={showNotFound}
            notFoundTitle={notFoundTitle}
            notFoundMessage={notFoundMessage}
            hideFooter={footerInModal}
          />
        )}

        {step === 1 && contactData && (
          <InternalTransferInfoStep
            contactData={contactData}
            linkValue={linkValue}
            onLinkValueChange={setLinkValue}
            onContinue={handleTransferInfoContinue}
            hideFooter={footerInModal}
            isSelfTransfer={isSelfDestination}
            selfTransferMessage={SELF_TRANSFER_MESSAGE}
            sourceAccountBalance={sourceAccountBalance}
            sourceAccountBalanceCents={sourceAccountBalanceCents}
            sourceAccountBalanceLoading={sourceAccountBalanceLoading}
            sourceAccountBalanceError={sourceAccountBalanceError}
            sourceAccountShowLimit={sourceAccountShowLimit}
            sourceAccountFormattedAvailable={sourceAccountFormattedAvailable}
            sourceAccountFormattedLimit={sourceAccountFormattedLimit}
          />
        )}

        {step === 2 && contactData && (
          <InternalTransferConfirmStep
            contactData={contactData}
            linkValue={linkValue}
            description={transferDescription}
          />
        )}

        {step === 3 && (
          <PixTransferPasswordStep
            ref={passwordStepRef}
            onSubmit={handleFinishTransaction}
            loading={isConfirming}
            hideFooter={footerInModal}
            error={passwordError}
            onDismissError={() => setPasswordError(null)}
          />
        )}
      </>
    ) : (
      <FinalStep
        inModal={inModal}
        hideToolbar={footerInModal}
        onClose={handleClose}
        onNewTransfer={resetFlow}
        transactionStatus={transactionSucceeded}
        transactionData={receiptData}
        sourceAccountLabel={sourceAccountLabel}
        sourceAccountBalance={sourceAccountBalance}
        sourceAccountBalanceLoading={sourceAccountBalanceLoading}
      />
    );

  if (inModal) {
    return <div className="w-full min-w-0">{wizardContent}</div>;
  }

  return (
    <div className="w-full min-w-0">
      <PageHeaderWithBack
        title="Transferir entre contas"
        subTitle={STEP_SUBTITLES[step]}
        onBack={step < 4 ? handleClose : undefined}
      />
      <div className="mt-4">{wizardContent}</div>
    </div>
  );
});
